import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye, EyeOff, Settings, X, Minus, ChevronDown, Check, Search, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useCanWrite } from '../components/Can';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
  type: 'YARN' | 'GREY';
  greyQualityId: number | null;
  greyQuality?: { id: number; name: string } | null;
  colour: string | null;
  pick: number | null;
  tenantId: number | null;
  recipes: QualityYarn[];
  createdAt: string;
  updatedAt: string;
}

interface QualityYarn {
  id: number;
  qualityId: number;
  yarnId: number;
  ratioKg: number;
  yarn: {
    id: number;
    name: string;
  };
}

interface Yarn {
  id: number;
  name: string;
  isActive: boolean;
}

interface QualityForm {
  name: string;
  isActive: boolean;
  type: 'YARN' | 'GREY';
  greyQualityId: number | null;
  colour: string;
  pick: number | null;
  recipes: RecipeForm[];
}

interface RecipeForm {
  yarnId: number;
  ratioKg: number;
}

// Payload sent to the API — colour/pick are nullable so they can be cleared, unlike the
// react-hook-form field types which stay string/number for the inputs.
type QualitySavePayload = Omit<QualityForm, 'colour' | 'pick'> & {
  colour: string | null;
  pick: number | null;
};

// Inline Yarn Select Dropdown with "Create New" option
const YarnSelectDropdown: React.FC<{
  yarns: Yarn[];
  value: number;
  onChange: (yarnId: number) => void;
  onCreateYarn: (name: string) => Promise<void>;
}> = ({ yarns, value, onChange, onCreateYarn }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newYarnName, setNewYarnName] = useState('');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 220 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedYarn = yarns.find((y) => y.id === value);

  const filteredYarns = yarns.filter((y) =>
    y.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsCreating(false);
        setSearch('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleCreateYarn = async () => {
    if (!newYarnName.trim()) return;
    setIsSubmitting(true);
    try {
      await onCreateYarn(newYarnName.trim());
      setNewYarnName('');
      setIsCreating(false);
      setIsOpen(false);
      setSearch('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        className="w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] flex items-center justify-between"
      >
        <span className={selectedYarn ? 'text-[#0E131B]' : 'text-[rgba(14,19,27,0.4)]'}>
          {selectedYarn ? selectedYarn.name : 'Select a yarn'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-[rgba(14,19,27,0.4)] flex-shrink-0" />
      </button>

      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed bg-white border border-[#E1E6EF] rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] max-h-64 overflow-hidden z-[10000]"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
          <div className="p-1.5 border-b border-[#F1F3F9]">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search yarns..."
              className="w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
            />
          </div>

          <div className="max-h-40 overflow-y-auto">
            {filteredYarns.length === 0 && !isCreating && (
              <div className="px-3 py-2 text-sm text-[rgba(14,19,27,0.4)]">No yarns found</div>
            )}
            {filteredYarns.map((yarn) => (
              <button
                key={yarn.id}
                type="button"
                onClick={() => {
                  onChange(yarn.id);
                  setIsOpen(false);
                  setSearch('');
                }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FC] flex items-center justify-between ${
                  yarn.id === value ? 'bg-[#F8F9FC] text-[#0055a9] font-medium' : 'text-[#0E131B]'
                }`}
              >
                <span>{yarn.name}</span>
                {yarn.id === value && <Check className="h-3.5 w-3.5 text-[#0055a9]" />}
              </button>
            ))}
          </div>

          <div className="border-t border-[#F1F3F9]">
            {isCreating ? (
              <div className="p-1.5 flex gap-1.5">
                <input
                  type="text"
                  value={newYarnName}
                  onChange={(e) => setNewYarnName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleCreateYarn(); }
                    if (e.key === 'Escape') { setIsCreating(false); setNewYarnName(''); }
                  }}
                  placeholder="Enter yarn name"
                  className="flex-1 border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
                  autoFocus
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={handleCreateYarn}
                  disabled={isSubmitting || !newYarnName.trim()}
                  className="px-3 py-2 text-xs font-medium text-white bg-[#0055a9] rounded-xl hover:bg-[#0D2680] disabled:opacity-50"
                >
                  {isSubmitting ? '...' : 'Add'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setIsCreating(true); setNewYarnName(search); }}
                className="w-full text-left px-3 py-2 text-sm text-[#0055a9] hover:bg-[#F8F9FC] font-medium flex items-center gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Create New Yarn
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const ic = "w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]";

// 3-dot actions dropdown for each row
const ActionsDropdown: React.FC<{
  quality: Quality;
  onManageRecipes: (q: Quality) => void;
  onEdit: (q: Quality) => void;
  onDelete: (q: Quality) => void;
}> = ({ quality, onManageRecipes, onEdit, onDelete }) => {
  const canWrite = useCanWrite('qualities');
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="p-1.5 rounded-lg text-[rgba(14,19,27,0.45)] hover:text-[#0E131B] hover:bg-[#F8F9FC] transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-[10000] w-40 bg-white border border-[#E1E6EF] rounded-xl shadow-lg py-1"
          style={{ top: pos.top, left: pos.left }}
        >
          <button
            onClick={() => { onManageRecipes(quality); setIsOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-2"
          >
            <Settings className="w-3.5 h-3.5" />
            Manage Recipes
          </button>
          <button
            onClick={() => { onEdit(quality); setIsOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-2"
          >
            <Edit className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => { onDelete(quality); setIsOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#E02D3C] hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      )}
    </>
  );
};

const Qualities: React.FC = () => {
  const { t } = useTranslation();
  const canWrite = useCanWrite('qualities');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuality, setEditingQuality] = useState<Quality | null>(null);
  const [deletingQuality, setDeletingQuality] = useState<Quality | null>(null);
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<Quality | null>(null);
  const [recipes, setRecipes] = useState<RecipeForm[]>([]);
  const [qualityRecipes, setQualityRecipes] = useState<RecipeForm[]>([
    { yarnId: 0, ratioKg: 0 }
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [qualityTab, setQualityTab] = useState<'YARN' | 'GREY'>('YARN'); // which list: Yarn or Grey
  const [groupByGrey, setGroupByGrey] = useState(false); // Yarn tab: group yarn qualities under their grey quality
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<QualityForm>({
    defaultValues: {
      recipes: [{ yarnId: 0, ratioKg: 0 }],
    },
  });

  const {
    register: registerRecipe,
    handleSubmit: handleRecipeSubmit,
    reset: resetRecipe,
    formState: { errors: recipeErrors },
  } = useForm<RecipeForm>();

  // Fetch qualities
  const { data: qualitiesResponse, isLoading, error } = useQuery({
    queryKey: ['qualities'],
    queryFn: () => apiService.get('/qualities'),
  });

  const allQualities: Quality[] = qualitiesResponse?.qualities || [];

  // Client-side filtering — the Yarn/Grey tab is the primary split.
  const filteredQualities = allQualities.filter((q: Quality) => {
    const matchesType = (q.type || 'YARN') === qualityTab;
    const matchesSearch = !searchQuery || q.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? q.isActive : !q.isActive);
    return matchesType && matchesSearch && matchesStatus;
  });
  const yarnCount = allQualities.filter((q: Quality) => (q.type || 'YARN') === 'YARN').length;
  const greyCount = allQualities.filter((q: Quality) => q.type === 'GREY').length;
  // Grey qualities (for the yarn-quality's "rolls up to" dropdown).
  const greyQualities = allQualities.filter((q: Quality) => q.type === 'GREY');

  // Yarn qualities grouped under their grey quality (for the "report according to grey quality" view).
  const greyGroups = qualityTab === 'YARN' && groupByGrey
    ? (() => {
        const map = new Map<string, { grey: string; items: Quality[] }>();
        filteredQualities.forEach((q) => {
          const key = q.greyQuality?.name || '— Unassigned —';
          if (!map.has(key)) map.set(key, { grey: key, items: [] });
          map.get(key)!.items.push(q);
        });
        return Array.from(map.values()).sort((a, b) => (a.grey === '— Unassigned —' ? 1 : b.grey === '— Unassigned —' ? -1 : a.grey.localeCompare(b.grey)));
      })()
    : null;

  const totalPages = Math.ceil(filteredQualities.length / pageSize);
  const paginatedQualities = filteredQualities.slice((page - 1) * pageSize, page * pageSize);
  const activeCount = allQualities.filter((q: Quality) => q.isActive).length;
  const inactiveCount = allQualities.filter((q: Quality) => !q.isActive).length;

  // Fetch yarns for recipe
  const { data: yarnsResponse } = useQuery({
    queryKey: ['yarns'],
    queryFn: () => apiService.get('/yarns'),
  });

  const yarns = yarnsResponse?.yarns || [];

  // Create quality mutation
  const createMutation = useMutation({
    mutationFn: (data: QualitySavePayload) => apiService.post('/qualities', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Quality created successfully');
      setIsModalOpen(false);
      setSelectedTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create quality');
    },
  });

  // Update quality mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QualitySavePayload }) =>
      apiService.put(`/qualities/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Quality updated successfully');
      setIsModalOpen(false);
      setEditingQuality(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update quality');
    },
  });

  // Delete quality mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/qualities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Quality deleted successfully');
      setDeletingQuality(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete quality');
    },
  });

  // Deactivate quality mutation
  const deactivateMutation = useMutation({
    mutationFn: (id: number) => apiService.put(`/qualities/${id}/deactivate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Quality status updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update quality status');
    },
  });

  // Add recipe mutation
  const addRecipeMutation = useMutation({
    mutationFn: ({ qualityId, yarnId, ratioKg }: { qualityId: number; yarnId: number; ratioKg: number }) =>
      apiService.post(`/qualities/${qualityId}/recipes`, { yarnId, ratioKg }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Recipe added successfully');
      resetRecipe();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add recipe');
    },
  });

  // Delete recipe mutation
  const deleteRecipeMutation = useMutation({
    mutationFn: ({ qualityId, recipeId }: { qualityId: number; recipeId: number }) =>
      apiService.delete(`/qualities/${qualityId}/recipes/${recipeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualities'] });
      toast.success('Recipe deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete recipe');
    },
  });

  // Create yarn mutation (inline from quality form)
  const createYarnMutation = useMutation({
    mutationFn: (name: string) => apiService.post('/yarns', { name, isActive: true }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['yarns'] });
      toast.success('Yarn created successfully');
      return data;
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create yarn');
    },
  });

  const addRecipeItem = () => {
    setQualityRecipes([...qualityRecipes, { yarnId: 0, ratioKg: 0 }]);
  };

  const removeRecipeItem = (index: number) => {
    if (qualityRecipes.length > 1) {
      const updatedRecipes = qualityRecipes.filter((_, i) => i !== index);
      setQualityRecipes(updatedRecipes);
    }
  };

  const updateRecipeItem = (index: number, field: keyof RecipeForm, value: number) => {
    const updatedRecipes = [...qualityRecipes];
    updatedRecipes[index] = { ...updatedRecipes[index], [field]: value };
    setQualityRecipes(updatedRecipes);
  };

  const onSubmit = (data: QualityForm) => {
    const validRecipes = qualityRecipes.filter(recipe => recipe.yarnId && recipe.ratioKg > 0);

    if (validRecipes.length === 0) {
      toast.error('Please add at least one valid recipe item');
      return;
    }

    if (!editingQuality && showCompanyColumn && !selectedTenantId) {
      toast.error('Please select a company');
      return;
    }

    const isYarn = (editingQuality ? editingQuality.type : qualityTab) === 'YARN';
    const formData = {
      ...data,
      // New quality → current tab's type; editing → keep the quality's existing type.
      type: editingQuality ? editingQuality.type : qualityTab,
      // Only a yarn quality rolls up to a grey quality.
      greyQualityId: isYarn ? (Number(data.greyQualityId) || null) : null,
      // Empty colour → null; non-numeric/blank pick (valueAsNumber gives NaN) → null.
      colour: data.colour?.trim() ? data.colour.trim() : null,
      pick: Number.isFinite(data.pick as number) ? data.pick : null,
      recipes: validRecipes,
      tenantId: (!editingQuality && showCompanyColumn) ? selectedTenantId : undefined,
    };

    if (editingQuality) {
      updateMutation.mutate({ id: editingQuality.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const onRecipeSubmit = (data: RecipeForm) => {
    if (selectedQuality) {
      addRecipeMutation.mutate({
        qualityId: selectedQuality.id,
        yarnId: data.yarnId,
        ratioKg: data.ratioKg,
      });
    }
  };

  const handleEdit = (quality: Quality) => {
    setEditingQuality(quality);
    reset({
      name: quality.name,
      isActive: quality.isActive,
      colour: quality.colour || '',
      pick: quality.pick ?? null,
      greyQualityId: quality.greyQualityId ?? null,
    });
    if (quality.recipes && quality.recipes.length > 0) {
      setQualityRecipes(quality.recipes.map(r => ({ yarnId: r.yarnId, ratioKg: r.ratioKg })));
    } else {
      setQualityRecipes([{ yarnId: 0, ratioKg: 0 }]);
    }
    setIsModalOpen(true);
  };

  const handleDelete = (quality: Quality) => {
    setDeletingQuality(quality);
  };

  const handleDeactivate = (quality: Quality) => {
    deactivateMutation.mutate(quality.id);
  };

  const handleManageRecipes = (quality: Quality) => {
    setSelectedQuality(quality);
    setRecipes(quality.recipes.map(r => ({ yarnId: r.yarnId, ratioKg: r.ratioKg })));
    setRecipeModalOpen(true);
  };

  const handleDeleteRecipe = (recipeId: number) => {
    if (selectedQuality) {
      deleteRecipeMutation.mutate({
        qualityId: selectedQuality.id,
        recipeId,
      });
    }
  };

  const confirmDelete = () => {
    if (deletingQuality) {
      deleteMutation.mutate(deletingQuality.id);
    }
  };

  const openModal = () => {
    setEditingQuality(null);
    setQualityRecipes([{ yarnId: 0, ratioKg: 0 }]);
    reset({ name: '', isActive: true, colour: '', pick: null, greyQualityId: null, recipes: [{ yarnId: 0, ratioKg: 0 }] });
    setSelectedTenantId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingQuality(null);
    setQualityRecipes([{ yarnId: 0, ratioKg: 0 }]);
    setSelectedTenantId(null);
    reset();
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto mb-3"></div>
        <p className="text-sm text-[rgba(14,19,27,0.45)]">Loading qualities...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-[#E02D3C]">Failed to load qualities. Please try again.</p>
      </div>
    );
  }

  const startRow = filteredQualities.length > 0 ? (page - 1) * pageSize + 1 : 0;
  const endRow = Math.min(page * pageSize, filteredQualities.length);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // Total columns (the Grey Quality column only shows on the Yarn tab; Company only in all-companies mode).
  const colCount = (qualityTab === 'YARN' ? 7 : 6) + (showCompanyColumn ? 1 : 0);
  const renderQualityRow = (quality: Quality, seq: number) => (
    <motion.tr key={quality.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD] transition-colors">
      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">{seq}</td>
      <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-[#0E131B]">{quality.name}</td>
      {qualityTab === 'YARN' && (
        <td className="px-4 py-4 whitespace-nowrap text-sm">
          {quality.greyQuality
            ? <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EDE9FE] text-[#6D28D9] border border-[#DDD6FE]">{quality.greyQuality.name}</span>
            : <span className="text-[rgba(14,19,27,0.3)]">—</span>}
        </td>
      )}
      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{quality.recipes?.length || 0}</td>
      <td className="px-4 py-4 text-sm text-[#0E131B] max-w-[350px]">
        {quality.recipes && quality.recipes.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {quality.recipes.map((r, i) => (
              <span key={i} className="inline-flex items-center bg-[#F8F9FC] border border-[#E1E6EF] rounded text-xs px-2 py-0.5 text-[#0E131B]">
                {r.yarn.name} <span className="text-[rgba(14,19,27,0.45)] ml-1">{r.ratioKg}kg</span>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-[rgba(14,19,27,0.3)]">No recipes</span>
        )}
      </td>
      <td className="px-4 py-4 text-center">
        <button
          onClick={() => handleDeactivate(quality)}
          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold cursor-pointer border transition-colors ${
            quality.isActive ? 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]' : 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]'
          }`}
          title={quality.isActive ? 'Click to deactivate' : 'Click to activate'}
        >
          {quality.isActive ? 'Active' : 'Inactive'}
        </button>
      </td>
      {showCompanyColumn && (
        <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">
          <CompanyCell tenantId={quality.tenantId} />
        </td>
      )}
      <td className="px-4 py-4 text-center">
        <ActionsDropdown quality={quality} onManageRecipes={handleManageRecipes} onEdit={handleEdit} onDelete={handleDelete} />
      </td>
    </motion.tr>
  );

  return (
    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-4">
      {/* Single white card container */}
      <div className="bg-white rounded-xl border border-[#E8EBF1]">
        {/* Header Bar */}
        <div className="flex items-center px-5 py-4 border-b border-[#E8EBF1] gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-[#1B202A]">Qualities</h1>

          {/* Yarn / Grey list toggle */}
          <div className="inline-flex rounded-xl border border-[#E1E6EF] bg-[#F8F9FC] p-0.5">
            {(['YARN', 'GREY'] as const).map((t) => (
              <button key={t} onClick={() => { setQualityTab(t); setPage(1); }}
                className={`px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-colors ${qualityTab === t ? 'bg-[#0055a9] text-white shadow-sm' : 'text-[rgba(14,19,27,0.6)] hover:text-[#0E131B]'}`}>
                {t === 'YARN' ? 'Yarn Quality' : 'Grey Quality'}
                <span className={`ml-1.5 text-[11px] ${qualityTab === t ? 'text-white/80' : 'text-[rgba(14,19,27,0.4)]'}`}>{t === 'YARN' ? yarnCount : greyCount}</span>
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-[rgba(14,19,27,0.4)] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search qualities..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="pl-9 pr-3 py-2 text-sm bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] w-52"
              />
            </div>
            {/* Group by grey (report) — Yarn tab only */}
            {qualityTab === 'YARN' && (
              <button onClick={() => setGroupByGrey((v) => !v)}
                title="Group yarn qualities under their grey quality"
                className={`px-3.5 py-2 text-sm font-semibold rounded-xl border transition-colors ${groupByGrey ? 'bg-[#EDE9FE] border-[#DDD6FE] text-[#6D28D9]' : 'bg-white border-[#E1E6EF] text-[rgba(14,19,27,0.6)] hover:bg-[#F8F9FC]'}`}>
                Group by grey
              </button>
            )}
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
              className="px-4 py-2 text-sm bg-white border border-[#E1E6EF] rounded-xl shadow-[1px_1px_3px_rgba(0,0,0,0.06)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {/* Primary Action */}
            <button onClick={openModal} disabled={!canWrite}
              title={!canWrite ? 'You have read-only access' : undefined}
              className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0D2680] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <Plus className="w-4 h-4" />
              New {qualityTab === 'YARN' ? 'Yarn' : 'Grey'} Quality
            </button>
          </div>
        </div>

        {/* Table */}
        {filteredQualities.length === 0 ? (
          <div className="p-12 text-center">
            <Settings className="w-10 h-10 text-[rgba(14,19,27,0.15)] mx-auto mb-3" />
            <p className="text-[rgba(14,19,27,0.45)] font-medium mb-1">
              {searchQuery || statusFilter !== 'all' ? 'No qualities match your filters' : `No ${qualityTab === 'YARN' ? 'yarn' : 'grey'} qualities yet`}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button onClick={openModal} className="mt-3 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-medium hover:bg-[#0D2680] transition-colors">
                Add Quality
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider w-14 bg-[#F8F9FC]">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Quality Name</th>
                    {qualityTab === 'YARN' && <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Grey Quality</th>}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider w-24 bg-[#F8F9FC]">Recipes</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Yarn Details</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider w-24 bg-[#F8F9FC]">Status</th>
                    {showCompanyColumn && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Company</th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider w-20 bg-[#F8F9FC]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {greyGroups
                    ? greyGroups.flatMap((grp) => [
                        <tr key={`grp-${grp.grey}`} className="bg-[#F1F3F9] border-b border-[#E8EBF1]">
                          <td colSpan={colCount} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#6D28D9]">
                            {grp.grey} <span className="text-[rgba(14,19,27,0.4)] ml-1">· {grp.items.length}</span>
                          </td>
                        </tr>,
                        ...grp.items.map((q: Quality, i: number) => renderQualityRow(q, i + 1)),
                      ])
                    : paginatedQualities.map((quality: Quality, idx: number) => renderQualityRow(quality, (page - 1) * pageSize + idx + 1))}
                </tbody>
              </table>
            </div>

            {/* Pagination (hidden in grouped report mode) */}
            {filteredQualities.length > 0 && !greyGroups && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1]">
                <div className="text-sm text-[rgba(14,19,27,0.5)]">
                  {startRow}-{endRow} of {filteredQualities.length}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-lg text-[rgba(14,19,27,0.5)] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {getPageNumbers().map((pg, i) =>
                    typeof pg === 'string' ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-[rgba(14,19,27,0.4)]">...</span>
                    ) : (
                      <button
                        key={pg}
                        onClick={() => setPage(pg)}
                        className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-colors ${
                          pg === page
                            ? 'bg-[#0055a9] text-white'
                            : 'text-[#0E131B] hover:bg-[#F8F9FC]'
                        }`}
                      >
                        {pg}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-1.5 rounded-lg text-[rgba(14,19,27,0.5)] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm text-[rgba(14,19,27,0.5)]">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="px-2 py-1 text-sm border border-[#E1E6EF] rounded-lg bg-white focus:outline-none"
                  >
                    {[10, 25, 50].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Quality Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-4">
          <div className="relative w-full max-w-2xl rounded-xl border border-[#E1E6EF] shadow-2xl bg-white">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#E8EBF1]">
              <h3 className="text-lg font-semibold text-[#1B202A]">
                {editingQuality ? 'Edit Quality' : 'Add New Quality'}
              </h3>
              <button onClick={closeModal} className="p-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#0E131B] hover:bg-[#F8F9FC] transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                {showCompanyColumn && !editingQuality && (
                  <div>
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Company</label>
                    <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                  </div>
                )}
                {/* Quality Info */}
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-8">
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Quality Name</label>
                    <input
                      {...register('name', { required: 'Quality name is required' })}
                      className={ic}
                      placeholder="Enter quality name"
                      autoFocus
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-[#E02D3C]">{errors.name.message}</p>
                    )}
                  </div>
                  <div className="col-span-4 flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        {...register('isActive')}
                        type="checkbox"
                        className="h-4 w-4 text-[#0055a9] focus:ring-[#0055a9] border-[#E1E6EF] rounded"
                      />
                      <span className="text-sm font-medium text-[#0E131B]">Active</span>
                    </label>
                  </div>
                </div>

                {/* Default Colour + Pick — auto-filled into taka rows when this quality is picked */}
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-8">
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Color</label>
                    <input
                      {...register('colour')}
                      className={ic}
                      placeholder="e.g. White, Red"
                    />
                  </div>
                  <div className="col-span-4">
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Pick</label>
                    <input
                      {...register('pick', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={ic}
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Grey Quality — a yarn quality rolls up to a grey quality (for reporting) */}
                {(editingQuality ? editingQuality.type : qualityTab) === 'YARN' && (
                  <div>
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Grey Quality <span className="text-xs font-normal text-[rgba(14,19,27,0.4)]">— rolls up to</span></label>
                    <select {...register('greyQualityId', { setValueAs: (v) => (v === '' || v == null ? null : Number(v)) })} className={ic}>
                      <option value="">— None —</option>
                      {greyQualities.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                    {greyQualities.length === 0 && <p className="text-xs text-[rgba(14,19,27,0.4)] mt-1">No grey qualities yet — add one under the Grey Quality tab first.</p>}
                  </div>
                )}

                {/* Yarn Recipes Section */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-[#0E131B]">Yarn Recipes</label>
                    <button
                      type="button"
                      onClick={addRecipeItem}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#0055a9] bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl hover:bg-[#EEF0F6] transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Row
                    </button>
                  </div>

                  <div className="border border-[#E1E6EF] rounded-xl overflow-visible">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-[#F8F9FC]">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase w-[55%]">Yarn</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase w-[30%]">Ratio (kg)</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase w-[15%]"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {qualityRecipes.map((recipe, index) => (
                          <tr key={index} className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD]">
                            <td className="px-4 py-3">
                              <YarnSelectDropdown
                                yarns={yarns}
                                value={recipe.yarnId}
                                onChange={(yarnId) => updateRecipeItem(index, 'yarnId', yarnId)}
                                onCreateYarn={async (name) => {
                                  const result: any = await createYarnMutation.mutateAsync(name);
                                  const newYarn = result?.yarn;
                                  if (newYarn?.id) {
                                    updateRecipeItem(index, 'yarnId', newYarn.id);
                                  }
                                }}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={recipe.ratioKg}
                                onChange={(e) => updateRecipeItem(index, 'ratioKg', parseFloat(e.target.value) || 0)}
                                className={ic}
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              {qualityRecipes.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeRecipeItem(index)}
                                  className="p-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-[#FEF2F2] transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E8EBF1] bg-[#F8F9FC]/50 rounded-b-xl">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0D2680] disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : editingQuality ? 'Update Quality' : 'Save Quality'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recipe Management Modal */}
      {recipeModalOpen && selectedQuality && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-4">
          <div className="relative w-full max-w-2xl rounded-xl border border-[#E1E6EF] shadow-2xl bg-white">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#E8EBF1]">
              <div>
                <h3 className="text-lg font-semibold text-[#1B202A]">Manage Recipes</h3>
                <p className="text-sm text-[rgba(14,19,27,0.5)] mt-0.5">Quality: <span className="font-medium text-[#0E131B]">{selectedQuality.name}</span></p>
              </div>
              <button onClick={() => setRecipeModalOpen(false)} className="p-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#0E131B] hover:bg-[#F8F9FC] transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Add New Recipe Form */}
              <form onSubmit={handleRecipeSubmit(onRecipeSubmit)} className="p-4 bg-[#F8F9FC] rounded-xl border border-[#E1E6EF]">
                <label className="block text-sm font-medium text-[#0E131B] mb-3">Add New Recipe</label>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-5">
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Yarn</label>
                    <select
                      {...registerRecipe('yarnId', { required: 'Yarn is required' })}
                      className={ic}
                    >
                      <option value="">Select a yarn</option>
                      {yarns.map((yarn: Yarn) => (
                        <option key={yarn.id} value={yarn.id}>{yarn.name}</option>
                      ))}
                    </select>
                    {recipeErrors.yarnId && (
                      <p className="mt-1 text-xs text-[#E02D3C]">{recipeErrors.yarnId.message}</p>
                    )}
                  </div>
                  <div className="col-span-4">
                    <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Ratio (kg)</label>
                    <input
                      {...registerRecipe('ratioKg', {
                        required: 'Ratio is required',
                        min: { value: 0.01, message: 'Must be > 0' }
                      })}
                      type="number"
                      step="0.01"
                      className={ic}
                      placeholder="0.00"
                    />
                    {recipeErrors.ratioKg && (
                      <p className="mt-1 text-xs text-[#E02D3C]">{recipeErrors.ratioKg.message}</p>
                    )}
                  </div>
                  <div className="col-span-3 flex items-end">
                    <button
                      type="submit"
                      disabled={addRecipeMutation.isPending}
                      className="w-full px-3 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0D2680] disabled:opacity-50 transition-colors"
                    >
                      {addRecipeMutation.isPending ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>
              </form>

              {/* Current Recipes */}
              <div>
                <label className="block text-sm font-medium text-[#0E131B] mb-2">Current Recipes ({selectedQuality.recipes?.length || 0})</label>
                {selectedQuality.recipes && selectedQuality.recipes.length > 0 ? (
                  <div className="border border-[#E1E6EF] rounded-xl overflow-hidden">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-[#F8F9FC]">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase">Yarn</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase">Ratio (kg)</th>
                          <th className="px-4 py-3 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedQuality.recipes.map((recipe: QualityYarn) => (
                          <tr key={recipe.id} className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD]">
                            <td className="px-4 py-4 text-sm font-medium text-[#0E131B]">{recipe.yarn.name}</td>
                            <td className="px-4 py-4 text-sm text-[#0E131B] tabular-nums">{recipe.ratioKg} kg</td>
                            <td className="px-4 py-4 text-center">
                              <button
                                onClick={() => handleDeleteRecipe(recipe.id)}
                                className="p-1 rounded-lg text-[rgba(14,19,27,0.4)] hover:text-[#E02D3C] hover:bg-[#FEF2F2] transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 text-center border border-[#E1E6EF] rounded-xl">
                    <p className="text-sm text-[rgba(14,19,27,0.4)]">No recipes defined yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-[#E8EBF1] bg-[#F8F9FC]/50 rounded-b-xl">
              <button
                onClick={() => setRecipeModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingQuality && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-4"
          tabIndex={-1} ref={el => el?.focus()}
          onKeyDown={e => { if (e.key === 'Escape') setDeletingQuality(null); if (e.key === 'Enter') { e.preventDefault(); confirmDelete(); } }}>
          <div className="relative w-full max-w-sm rounded-xl border border-[#E1E6EF] shadow-2xl bg-white">
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-[#E02D3C]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1B202A]">Delete Quality</h3>
                  <p className="text-sm text-[rgba(14,19,27,0.5)]">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-[rgba(14,19,27,0.65)] mb-6">
                Are you sure you want to delete <span className="font-semibold text-[#0E131B]">"{deletingQuality.name}"</span>?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDeletingQuality(null)}
                  className="px-4 py-2 text-sm font-medium text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[#E02D3C] rounded-xl hover:bg-[#C82333] disabled:opacity-50 transition-colors"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Qualities;
